import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";

export type ContextToastType = "success" | "info" | "error";

export interface ContextToastAction {
  label: string;
  callback: () => void;
}

// Polaris Toast (@shopify/polaris 13.9.5, Frame/Toast): one short text, an optional action and the close button.
// "error" is the critical toast, only for connection errors; it leads with the alert icon.
@Component({
  selector: "app-context-toast",
  templateUrl: "./context-toast.component.html",
  styleUrls: ["./context-toast.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContextToastComponent implements AfterViewInit, OnDestroy {
  public animation: "out" | "in" = "in";
  @Input() public title: string;
  @Input() public type: ContextToastType = "success";
  /** Null keeps the toast until it is closed. */
  @Input() public duration: number | null;
  @Input() public action: ContextToastAction | null = null;

  /** Time is up, or the merchant closed it or used its action. */
  @Output() public readonly dismiss = new EventEmitter<void>();

  private remaining = 0;
  private startedAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  public ngAfterViewInit(): void {
    if (!this.duration) return;
    this.remaining = this.duration;
    this.resume();
  }

  public ngOnDestroy(): void {
    this.pause();
  }

  // Like Polaris, the toast waits while the pointer is over it
  public pause(): void {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.remaining -= Date.now() - this.startedAt;
  }

  public resume(): void {
    if (!this.duration || this.timer) return;
    this.startedAt = Date.now();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.close();
    }, this.remaining);
  }

  public close(): void {
    this.dismiss.emit();
  }

  public runAction(): void {
    this.action?.callback();
    this.close();
  }
}
